"use client";

import { FileBox, PaintBucket } from "lucide-react";
import { ACCESS_CHILDREN, BEXIO_CHILDREN, ESCALATIONS_CHILDREN, ONBOARDING_CHILDREN, PROJECTS_CHILDREN, hasPermission } from "./nav-config";

export function useSidebarGroups({
  permissions, isPending, kind, sessionsUnlocked, isTrainer, badges, pathname,
}: {
  permissions: string[];
  isPending?: boolean;
  kind: "MEMBER" | "GUEST";
  sessionsUnlocked: boolean;
  isTrainer: boolean;
  badges: Record<string, number>;
  pathname: string;
}) {
  const escalationsActive = pathname.startsWith("/escalations");
  function isEscalationsChildActive(href: string): boolean {
    if (href === "/escalations") {
      return escalationsActive && !pathname.startsWith("/escalations/insights") && !pathname.startsWith("/escalations/runbooks");
    }
    return pathname.startsWith(href);
  }
  const escalationsChildren = [
    ...ESCALATIONS_CHILDREN.filter(c => hasPermission(permissions, c.required)).map((c) => ({ ...c, active: isEscalationsChildActive(c.href) })),
  ];

  const onboardingActive = pathname.startsWith("/training");
  
  function isOnboardingChildActive(href: string): boolean {
    if (href === "/training") {
      return onboardingActive && 
        !pathname.startsWith("/training/my") &&
        !pathname.startsWith("/training/sessions") &&
        !pathname.startsWith("/training/library") &&
        !pathname.startsWith("/training/programs") &&
        !pathname.startsWith("/training/trainees") &&
        !pathname.startsWith("/training/credentials");
    }
    return pathname.startsWith(href);
  }
  const onboardingChildrenRaw = ONBOARDING_CHILDREN.filter(c => hasPermission(permissions, c.required) && (!isPending && kind !== "GUEST") && (c.href !== "/training/trainees" || isTrainer || hasPermission(permissions, ["onboarding:own", "onboarding:insights"])));
  const onboardingChildren = onboardingChildrenRaw.filter(c => {
    const locked = !sessionsUnlocked && !hasPermission(permissions, ["onboarding:library_manage"]);
    return c.href !== "/training/sessions" || locked || (badges["/training/sessions"] ?? 0) > 0 || hasPermission(permissions, ["onboarding:own"]);
  }).map((c) => {
    const locked = c.href === "/training/sessions" && !sessionsUnlocked && !hasPermission(permissions, ["onboarding:library_manage"]);
    return {
      ...c,
      active: isOnboardingChildActive(c.href),
      locked
    };
  });

  const bexioActive = pathname.startsWith("/bexio");
  function isBexioChildActive(href: string): boolean {
    if (href === "/bexio") return pathname === "/bexio";
    return pathname.startsWith(href);
  }
  const bexioChildren = BEXIO_CHILDREN.filter(
    (c) => hasPermission(permissions, c.required) && (!isPending && kind !== "GUEST")
  ).map((c) => ({
    ...c,
    active: isBexioChildActive(c.href),
  }));

  const projectsActive = pathname.startsWith("/projects");
  const projectSubpages = PROJECTS_CHILDREN.filter((c) => c.href !== "/projects").map((c) => c.href);
  function isProjectsChildActive(href: string): boolean {
    // "All projects" also covers a project's own pages and the new-project form.
    if (href === "/projects") return projectsActive && !projectSubpages.some((h) => pathname.startsWith(h));
    return pathname.startsWith(href);
  }
  const projectsChildren = PROJECTS_CHILDREN.filter(
    (c) => hasPermission(permissions, c.required) && (!isPending && kind !== "GUEST")
  ).map((c) => ({ ...c, active: isProjectsChildActive(c.href) }));

  const accessActive = pathname === "/admin/access" || pathname.startsWith("/admin/access-requests") || pathname.startsWith("/admin/roles") || pathname.startsWith("/admin/access-log") || pathname.startsWith("/admin/activity-log");

  const libraryActive = pathname.startsWith("/library");
  
  const libraryChildren = [];
  if (hasPermission(permissions, ["assets:view"])) {
    libraryChildren.push({
      href: "/library/assets",
      label: "Assets",
      icon: FileBox,
      active: pathname.startsWith("/library/assets")
    });
    libraryChildren.push({
      href: "/library/css-codes",
      label: "CSS Codes",
      icon: PaintBucket,
      active: pathname.startsWith("/library/css-codes")
    });
  }
  const accessChildren = ACCESS_CHILDREN.filter(c => hasPermission(permissions, c.required)).map((c) => ({ ...c, active: c.href === "/admin/access" ? pathname === "/admin/access" : pathname.startsWith(c.href) }));
  return {
    escalationsActive, escalationsChildren, onboardingActive, onboardingChildren,
    bexioActive, bexioChildren, projectsActive, projectsChildren,
    accessActive, accessChildren, libraryActive, libraryChildren,
  };
}

export type SidebarGroups = ReturnType<typeof useSidebarGroups>;
